import { useRef } from "react";
import { idGenerator } from "generate-custom-id";

import EntityAccordion from "./EntityAccordion";
import {
  useExpandEntityMutation,
  useShrinkEntityMutation,
} from "../../hooks/mutations";
import classes from "./Entity.module.scss";

/**
 * Recursive entity node. Wraps an EntityAccordion plus its expand-children,
 * rendering the connector from the parent's right anchor (startPointId) to
 * this accordion's left, and inner displayed entities to the right.
 * diplayedEntityId typo is preserved for the API contract.
 */
export default function Entity({
  ruleId,
  queryId,
  data,
  name,
  diplayedEntityId,
  parentDisplayedEntityId,
  innerDisplayedEntities,
  startPointId,
  from,
  allowRemove,
  isTopLevel,
  parentRef,
  onRemove,
  canEdit = true,
}) {
  const open = !!diplayedEntityId;
  const leftId = useRef(idGenerator("example", 4)).current;
  const rightId = useRef(idGenerator("example", 4)).current;
  const entityRef = useRef(null);

  const { mutate: expandEntity } = useExpandEntityMutation({ ruleId, queryId });
  const { mutate: shrinkEntity } = useShrinkEntityMutation({ ruleId, queryId });

  const handleOpen = () => {
    if (from === "entity-drop-target") {
      expandEntity({
        entityId: data.id,
        parentDisplayedEntityId,
        queryId,
      });
    }
    // No manual arrow update needed — the ConnectorLayer tracks layout
    // changes (incl. the Collapse animation) via observers.
  };

  const handleClose = () => {
    shrinkEntity(diplayedEntityId);
  };

  return (
    <div className={classes.entitiesSection}>
      <div className={classes.entityWrapper} ref={entityRef}>
        {/* Connector declared on the accordion's left anchor (from={startPointId}). */}
        <EntityAccordion
          ruleId={ruleId}
          queryId={queryId}
          data={data}
          name={name}
          open={open}
          hasConnections={!!innerDisplayedEntities?.length}
          startPointId={startPointId}
          leftId={leftId}
          rightId={rightId}
          allowRemove={allowRemove}
          isTopLevel={isTopLevel}
          diplayedEntityId={diplayedEntityId}
          canEdit={canEdit}
          onOpen={handleOpen}
          onClose={handleClose}
          onRemove={onRemove}
        />
      </div>

      {open && innerDisplayedEntities?.length > 0 && (
        <div className={classes.innerGroup}>
          {innerDisplayedEntities.map((inner, i) => (
            <Entity
              // Stable key: must NOT change when the relation is
              // expanded/collapsed. Keying on diplayedEntityId caused a
              // remount on every expand/collapse, which regenerated the
              // connector anchor ids. Index is stable here — expand/
              // collapse toggles a field on an existing slot without
              // reordering the array.
              key={inner.ruleEntityId ?? inner.entity?.id ?? i}
              ruleId={ruleId}
              queryId={queryId}
              data={inner.entity || inner}
              name={inner.name}
              diplayedEntityId={inner.diplayedEntityId}
              parentDisplayedEntityId={diplayedEntityId}
              innerDisplayedEntities={inner.innerDisplayedEntities}
              startPointId={rightId}
              from={from}
              parentRef={parentRef}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}
